import React, {useEffect, useMemo, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Button,
    Card,
    Chip,
    IconButton,
    Menu, MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import useQueryParams from "../../utils/hooks/useQueryParams";
import useSWR from "swr";
import {Season} from "../../schemas/seasons/seasonsSchema";
import {Race} from "../../schemas/races/racesSchema";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {useAuth} from "../../utils/hooks/useAuth";
import UserRole from "../../schemas/user/roles";
import dayjs from "dayjs";
import {useNotification} from "../../utils/hooks/useNotification";
import {useConfirm} from "material-ui-confirm";
import ResultsTableView from "./ResultsTableView";
import {CategoryShort} from "../../schemas/category/categoriesSchema";
import useMediaQuery from "@mui/material/useMediaQuery";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const Results: React.FC = () => {
    const {hasPermission} = useAuth();
    const {showNotification} = useNotification();
    const confirm = useConfirm();
    const {getQueryParam, setQueryParam, removeQueryParam, searchParams} = useQueryParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const [season, setSeason] = useState<Season | null>(null);
    const [resultType, setResultType] = useState<"season" | "race">("season");
    const [selectedRace, setSelectedRace] = useState<Race | null>(null);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [editMode, setEditMode] = useState<boolean>(false);

    const [mutateFunctions, setMutateFunctions] = useState<{
        mutateRaceOverall: () => Promise<any>,
        mutateRaceCategory: () => Promise<any>,
        mutateSeasonOverall: () => Promise<any>,
        mutateSeasonCategory: () => Promise<any>,
    } | null>(null);

    const resultTypes = useMemo(() => [
        {label: "Sezóna", value: "season"},
        {label: "Závod", value: "race"}
    ], []);

    const {data: categories} = useSWR<CategoryShort[]>(`/categories`, getFetcher);

    const {data: seasons} = useSWR<Season[]>(`/seasons/all`, getFetcher);

    const {
        data: races,
        isLoading: isLoadingRaces,
        mutate: mutateRaces
    } = useSWR<Race[]>(season ? `/seasons/${season.id}/races` : null, getFetcher);

    const sortedSeasons = useMemo(() => {
        return seasons?.sort((a, b) => b.year - a.year) ?? [];
    }, [seasons]);

    const displayedRaces = useMemo(() => {
        const finishedRaces = races?.filter(race => race.status === "FINISHED")
            .sort((a, b) => dayjs(a.date).diff(b.date));
        if (hasPermission(UserRole.ORGANIZER)) {
            return finishedRaces;
        }
        return finishedRaces?.filter(race => race.resultStatus === "PUBLISHED");
    }, [hasPermission, races]);

    useEffect(() => {
        if (!seasons) return;

        // SEASON
        const seasonParam = getQueryParam("season");
        let chosenSeason: Season | null = null;
        if (seasonParam) {
            chosenSeason = seasons.find(s => String(s.year) === seasonParam) ?? null;
        }
        if (!chosenSeason) {
            chosenSeason = seasons.reduce((prev, current) => (prev.year > current.year ? prev : current), seasons[0]);
            if (chosenSeason) setQueryParam("season", chosenSeason.year);
        }
        setSeason(chosenSeason ?? null);

        // TYPE
        const typeParam = getQueryParam("type") ?? "season";
        if (resultTypes.some(t => t.value === typeParam)) {
            setResultType(typeParam as "season" | "race");
        } else {
            setResultType("season");
            setQueryParam("type", "season");
        }

        // RACE
        if (chosenSeason && resultType === "race") {
            const raceParam = getQueryParam("race");
            if (raceParam && displayedRaces) {
                const r = displayedRaces.find(r => String(r.id) === raceParam);
                setSelectedRace(r ?? null);
            } else {
                setSelectedRace(null);
            }
        } else {
            setSelectedRace(null);
        }

        // TAB
        const tabParam = getQueryParam("tab");
        if (tabParam) {
            const tabNumber = categories?.find(c => String(c.categoryNumber) === tabParam)?.categoryNumber ?? 0;
            setSelectedTab(tabNumber);
        } else {
            setSelectedTab(0);
            setQueryParam("tab", 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, seasons, displayedRaces, resultTypes, resultType]);

    const tabs = useMemo(() => [
        {key: 0, label: "Celkové"},
        ...categories?.map(category => ({
            key: category.categoryNumber,
            label: category.name
        })) ?? []
    ], [categories]);

    const handleSeasonChange = (season: Season | null) => {
        setSeason(season);
        setQueryParam("season", season?.year ?? "");

        setResultType("season");
        setQueryParam("type", "season");
        setSelectedRace(null);
        removeQueryParam("race");
        setSelectedTab(0);
        setQueryParam("tab", 0);
        setEditMode(false);
    };

    const handleResultTypeChange = (value: { label: string, value: "season" | "race" } | null) => {
        setResultType(value?.value ?? "season");
        setQueryParam("type", value?.value ?? "season");

        setSelectedRace(null);
        removeQueryParam("race");
        setSelectedTab(0);
        setQueryParam("tab", 0);
        setEditMode(false);
    };

    const handleRaceChange = (race: Race | null) => {
        setSelectedRace(race);
        if (race) {
            setQueryParam("race", race.id);
        } else {
            removeQueryParam("race");
        }
        setSelectedTab(0);
        setQueryParam("tab", 0);
        setEditMode(false);
    };

    const handleTabChange = (tab: number) => {
        setSelectedTab(tab);
        setQueryParam("tab", tab);
    };

    const safeDelete = async (url: string) => {
        try {
            await deleteFetcher(url);
        } catch (e: any) {
            if (e?.response?.status !== 404) throw e;
        }
    };

    const generateResults = async (url: string) => {
        await postFetcher(url, {});
    };

    const setRaceResultStatus = async (raceId: number, status: "PUBLISHED" | "UNPUBLISHED") => {
        await putFetcher(`/races/${raceId}/status/results`, {resultStatus: status});
    };

    const handleRecalculateRaceResults = async () => {
        if (!selectedRace || !season) return;
        const isPublished = selectedRace.resultStatus === "PUBLISHED";

        let confirmed = false;
        await confirm({
            title: "Opravdu chcete přepočítat výsledky závodu?",
            description: (
                <Stack>
                    <Typography>Dojde ke smazání a znovuvytvoření výsledků tohoto závodu.</Typography>
                    {isPublished && (
                        <Typography>Pozn.: Výsledky jsou publikované, proto dojde i k přepočtu výsledků
                            sezóny.</Typography>
                    )}
                </Stack>
            ),
            confirmationText: "Přepočítat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(() => {
            confirmed = true
        }).catch(() => false);

        if (!confirmed) return;

        try {
            await safeDelete(`/races/${selectedRace.id}/results`);
            await generateResults(`/races/${selectedRace.id}/results/generate`);
            showNotification("Výsledky závodu byly úspěšně přepočítány.", "success");
            if (mutateFunctions) {
                await Promise.all([
                    mutateFunctions.mutateRaceOverall(),
                    mutateFunctions.mutateRaceCategory()
                ]);
            }
        } catch (e) {
            showNotification("Chyba při přepočtu výsledků závodu.", "error");
            console.error(e);
            return;
        }

        if (isPublished) {
            try {
                await safeDelete(`/seasons/${season.id}/results`);
                await generateResults(`/seasons/${season.id}/results/generate`);
                showNotification("Sezónní výsledky byly také přepočítány.", "success");
                if (mutateFunctions) {
                    await Promise.all([
                        mutateFunctions.mutateSeasonOverall(),
                        mutateFunctions.mutateSeasonCategory()
                    ]);
                }
            } catch (e) {
                showNotification("Chyba při přepočtu sezónních výsledků.", "error");
                console.error(e);
            }
        }
    };

    const handleRecalculateSeasonResults = async () => {
        if (!season) return;

        let confirmed = false;
        await confirm({
            title: "Opravdu chcete přepočítat výsledky sezóny?",
            description: "Dojde ke smazání a znovuvytvoření všech sezónních výsledků.",
            confirmationText: "Přepočítat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(() => {
            confirmed = true
        }).catch(() => false);

        if (!confirmed) return;

        try {
            await safeDelete(`/seasons/${season.id}/results`);
            await generateResults(`/seasons/${season.id}/results/generate`);
            showNotification("Sezónní výsledky byly úspěšně přepočítány.", "success");
            if (mutateFunctions) {
                await Promise.all([
                    mutateFunctions.mutateSeasonOverall(),
                    mutateFunctions.mutateSeasonCategory()
                ]);
            }
        } catch (e) {
            showNotification("Chyba při přepočtu sezónních výsledků.", "error");
            console.error(e);
        }
    };

    const handlePublishRaceResults = async () => {
        if (!selectedRace || !season) return;

        let confirmed = false;
        await confirm({
            title: "Publikovat výsledky závodu?",
            description: (
                <Stack>
                    <Typography>Výsledky závodu budou zveřejněny a zároveň dojde k přepočtu výsledků
                        sezóny.</Typography>
                </Stack>
            ),
            confirmationText: "Publikovat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "success"},
        }).then(() => {
            confirmed = true
        }).catch(() => false);

        if (!confirmed) return;

        try {
            await setRaceResultStatus(selectedRace.id, "PUBLISHED");
            showNotification("Výsledky závodu byly publikovány.", "success");
        } catch (e) {
            showNotification("Chyba při publikaci výsledků závodu.", "error");
            console.error(e);
            return;
        }

        try {
            await safeDelete(`/seasons/${season.id}/results`);
            await generateResults(`/seasons/${season.id}/results/generate`);
            showNotification("Sezónní výsledky byly přepočítány.", "success");
            mutateRaces();
            if (mutateFunctions) {
                await Promise.all([
                    mutateFunctions.mutateSeasonOverall(),
                    mutateFunctions.mutateSeasonCategory()
                ]);
            }
        } catch (e) {
            showNotification("Chyba při přepočtu sezónních výsledků.", "error");
            console.error(e);
        }
    };

    const handleUnpublishRaceResults = async () => {
        if (!selectedRace || !season) return;

        let confirmed = false;
        await confirm({
            title: "Zrušit publikaci výsledků závodu?",
            description: (
                <Stack>
                    <Typography>Výsledky závodu budou staženy z veřejného zobrazení. Dojde k přepočtu výsledků sezóny,
                        aby tento závod nebyl zahrnut.</Typography>
                </Stack>
            ),
            confirmationText: "Zrušit publikaci",
            cancellationText: "Zavřít",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(() => {
            confirmed = true
        }).catch(() => false);

        if (!confirmed) return;

        try {
            await setRaceResultStatus(selectedRace.id, "UNPUBLISHED");
            showNotification("Publikace výsledků závodu byla zrušena.", "success");
        } catch (e) {
            showNotification("Chyba při zrušení publikace výsledků závodu.", "error");
            console.error(e);
            return;
        }

        try {
            await safeDelete(`/seasons/${season.id}/results`);
            await generateResults(`/seasons/${season.id}/results/generate`);
            showNotification("Sezónní výsledky byly přepočítány.", "success");
            mutateRaces();
            if (mutateFunctions) {
                await Promise.all([
                    mutateFunctions.mutateSeasonOverall(),
                    mutateFunctions.mutateSeasonCategory()
                ]);
            }
        } catch (e) {
            showNotification("Chyba při přepočtu sezónních výsledků.", "error");
            console.error(e);
        }
    };

    if (!isMobile) {
        return (
            <Stack direction="row" spacing={2}>
                <Stack direction="column" spacing={2} alignItems="top"
                       justifyContent="flex-start" sx={{width: "15%"}}>
                    <Typography component="h1" variant="h4">
                        Výsledky
                    </Typography>
                    <Card sx={{p: 2}}>
                        <Stack direction="column" spacing={2} alignItems="top"
                               justifyContent="flex-start" sx={{width: "100%"}}>
                            <Autocomplete
                                options={sortedSeasons}
                                value={season}
                                getOptionLabel={option => option.year.toString()}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                onChange={(_, value) => handleSeasonChange(value)}
                                renderInput={params => (
                                    <TextField {...params} label="Výběr sezóny" variant="standard"/>
                                )}
                                loading={!seasons}
                            />
                            <Autocomplete
                                options={resultTypes}
                                value={resultTypes.find(opt => opt.value === resultType) ?? null}
                                getOptionLabel={option => option.label}
                                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                onChange={(_, value) => handleResultTypeChange(value as {
                                    label: string,
                                    value: "season" | "race"
                                } | null)}
                                renderInput={params => (
                                    <TextField {...params} label="Typ výsledků" variant="standard"/>
                                )}
                            />
                            <Autocomplete
                                options={displayedRaces ?? []}
                                value={selectedRace}
                                getOptionLabel={option => `${option.name} - ${dayjs(option.date).format("DD.MM.YYYY")}`}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                loading={isLoadingRaces}
                                onChange={(_, value) => handleRaceChange(value)}
                                disabled={!season || resultType !== "race"}
                                renderInput={params => (
                                    <TextField {...params} label="Výběr závodu" variant="standard"/>
                                )}
                            />
                            {hasPermission(UserRole.ORGANIZER) && resultType === "race" && selectedRace && (
                                selectedRace.resultStatus === "UNPUBLISHED" ? (
                                    <Chip
                                        label="Výsledky nepublikovány"
                                        color="error"
                                    />) : (
                                    <Chip
                                        label="Výsledky publikovány"
                                        color="success"
                                    />
                                )
                            )}
                        </Stack>
                    </Card>
                    {hasPermission(UserRole.ORGANIZER) && (
                        <Card sx={{p: 2}}>
                            <Typography>
                                Možnosti akcí
                            </Typography>
                            <Stack direction="column" spacing={2} alignItems="top"
                                   justifyContent="flex-start" sx={{width: "100%"}}>
                                {resultType === "season" ? (
                                    <Button variant="contained" color="primary"
                                            onClick={handleRecalculateSeasonResults}>
                                        Přepočítat výsledky sezóny
                                    </Button>) : (
                                    selectedRace && (
                                        <Button variant="contained" color="primary"
                                                onClick={handleRecalculateRaceResults}>
                                            Přepočítat výsledky závodu
                                        </Button>)
                                )}
                                {resultType === "race" && selectedRace && (
                                    <>
                                        {selectedRace.resultStatus === "UNPUBLISHED" ? (
                                            <Button variant="contained" color="success"
                                                    onClick={handlePublishRaceResults}>
                                                Publikovat výsledky závodu
                                            </Button>) : (
                                            <Button variant="outlined" color="error"
                                                    onClick={handleUnpublishRaceResults}>
                                                Zrušit publikaci výsledků závodu
                                            </Button>
                                        )}
                                        {!editMode ? (
                                            <Button variant="outlined" color="primary" onClick={() => {
                                                confirm({
                                                    title: "Opravdu chcete upravovat vypočítané výsledky?",
                                                    description: "Pokud výsledky závodu manuálně upravíte, je zapotřebí ručně spustit přepočítání výsledků sezóny.",
                                                    confirmationText: "Upravovat",
                                                    cancellationText: "Zavřít",
                                                    confirmationButtonProps: {variant: "contained", color: "error"},
                                                }).then(() => {
                                                    setEditMode(true);
                                                }).catch(() => false);
                                            }}>
                                                Upravit výsledky závodu
                                            </Button>) : (
                                            <Button variant="contained" color="error"
                                                    onClick={() => setEditMode(false)}>
                                                Ukončit úpravy výsledků závodu
                                            </Button>
                                        )}
                                    </>
                                )}
                                {resultType === "race" && !selectedRace && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Vyberte závod pro další akce
                                    </Typography>
                                )}
                            </Stack>
                        </Card>
                    )}
                </Stack>
                <Stack direction="column" spacing={2} alignItems="top"
                       justifyContent="flex-start" sx={{width: "85%"}}>
                    <ResultsTableView
                        season={season}
                        race={selectedRace}
                        resultType={resultType}
                        selectedTab={selectedTab}
                        editMode={editMode}
                        tabs={tabs}
                        onTabChange={handleTabChange}
                        categories={categories ?? []}
                        races={displayedRaces ?? []}
                    />
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">Výsledky</Typography>
                {hasPermission(UserRole.ORGANIZER) && (
                    <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
                        <MoreVertIcon/>
                    </IconButton>
                )}
                <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
                    {resultType === "season" && (
                        <MenuItem onClick={() => {
                            handleRecalculateSeasonResults();
                            setAnchorEl(null);
                        }}>
                            Přepočítat výsledky sezóny
                        </MenuItem>
                    )}
                    {resultType === 'race' && selectedRace && (
                        <>
                            <MenuItem onClick={() => {
                                handleRecalculateRaceResults();
                                setAnchorEl(null);
                            }}>
                                Přepočítat výsledky závodu
                            </MenuItem>
                            {selectedRace.resultStatus === 'UNPUBLISHED' ? (
                                <MenuItem onClick={() => {
                                    handlePublishRaceResults();
                                    setAnchorEl(null);
                                }}>
                                    Publikovat výsledky závodu
                                </MenuItem>
                            ) : (
                                <MenuItem onClick={() => {
                                    handleUnpublishRaceResults();
                                    setAnchorEl(null);
                                }}>
                                    Zrušit publikaci výsledků závodu
                                </MenuItem>
                            )}
                            {!editMode ? (
                                <MenuItem onClick={() => {
                                    confirm({
                                        title: 'Opravdu chcete upravovat vypočítané výsledky?',
                                        description: 'Pokud výsledky závodu manuálně upravíte, je zapotřebí ručně spustit přepočítání výsledků sezóny.',
                                        confirmationText: 'Upravovat',
                                        cancellationText: 'Zavřít',
                                        confirmationButtonProps: {variant: 'contained', color: 'error'},
                                    }).then(() => {
                                        setEditMode(true);
                                        setAnchorEl(null);
                                    }).catch(() => {
                                    });
                                }}>
                                    Upravit výsledky závodu
                                </MenuItem>
                            ) : (
                                <MenuItem onClick={() => {
                                    setEditMode(false);
                                    setAnchorEl(null);
                                }}>
                                    Ukončit úpravy výsledků závodu
                                </MenuItem>
                            )}
                        </>
                    )}
                    {resultType === "race" && !selectedRace && (
                        <MenuItem onClick={() => setAnchorEl(null)}>
                            Vyberte závod pro další akce
                        </MenuItem>
                    )}
                </Menu>
            </Stack>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant="h6">Specifikace výsledků</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={seasons ?? []}
                            value={season}
                            getOptionLabel={option => option.year.toString()}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            onChange={(_, value) => handleSeasonChange(value)}
                            renderInput={params => (
                                <TextField {...params} label="Výběr sezóny" variant="standard"/>
                            )}
                        />
                        <Autocomplete
                            options={resultTypes}
                            value={resultTypes.find(opt => opt.value === resultType) ?? null}
                            getOptionLabel={option => option.label}
                            isOptionEqualToValue={(opt, val) => opt.value === val.value}
                            onChange={(_, value) => handleResultTypeChange(value as {
                                label: string,
                                value: "season" | "race"
                            } | null)}
                            renderInput={params => (
                                <TextField {...params} label="Typ výsledků" variant="standard"/>
                            )}
                        />
                        {resultType === "race" && (
                            <Autocomplete
                                options={displayedRaces ?? []}
                                value={selectedRace}
                                getOptionLabel={option => `${option.name} - ${dayjs(option.date).format("DD.MM.YYYY")}`}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                onChange={(_, value) => handleRaceChange(value)}
                                disabled={!season || resultType !== "race"}
                                renderInput={params => (
                                    <TextField {...params} label="Výběr závodu" variant="standard"/>
                                )}
                            />
                        )}
                        <Autocomplete
                            options={tabs}
                            value={tabs.find(opt => opt.key === selectedTab) ?? tabs[0]}
                            getOptionLabel={option => option.label}
                            isOptionEqualToValue={(opt, val) => opt.key === val.key}
                            onChange={(_, value) => handleTabChange(value?.key ?? 0)}
                            renderInput={params => (
                                <TextField {...params} label="Kategorie" variant="standard"/>
                            )}
                        />
                        {hasPermission(UserRole.ORGANIZER) && resultType === "race" && selectedRace && (
                            selectedRace.resultStatus === "UNPUBLISHED" ? (
                                <Chip label="Výsledky nepublikovány" color="error"/>
                            ) : (
                                <Chip label="Výsledky publikovány" color="success"/>
                            )
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>
            <ResultsTableView
                season={season}
                race={selectedRace}
                resultType={resultType}
                selectedTab={selectedTab}
                editMode={editMode}
                tabs={tabs}
                onTabChange={handleTabChange}
                categories={categories ?? []}
                races={displayedRaces ?? []}
                isMobile={isMobile}
                onMutateReady={setMutateFunctions}
            />
        </Stack>
    );
};

export default Results;
